import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';

import { appChat, appChatApi } from 'modules/app-chat';
import { customNotification, customNotificationApi } from 'modules/app-custom-notification';
import { appDashboard, appDashboardApi } from 'modules/app-dashboard';
import { appDataExport, appDataExportApi } from 'modules/app-data-export';
import { appInvitation, appInvitationApi } from 'modules/app-invitation';
import { appNotification, appNotificationApi } from 'modules/app-notification';
import { appTerms, appTermsApi } from 'modules/app-terms';
import { authSession, authSessionApi } from 'modules/auth-session';
import { companies, companiesApi } from 'modules/companies';
import { companyType, companyTypeApi } from 'modules/company-type';
import {
  eecsCancellations,
  eecsCancellationsApi,
  eecsCommon,
  eecsCommonApi,
  eecsCompanyApi,
  eecsDashboard,
  eecsDashboardApi,
  eecsFees,
  eecsFeesApi,
  eecsForward,
  eecsForwardApi,
  eecsGoCertificate,
  eecsGoCertificateApi,
  eecsPackage,
  eecsPackageApi,
  eecsProducts,
  eecsProductsApi,
  eecsTransaction,
  eecsTransactionApi,
  eecsWhiteList,
  eecsWhiteListApi,
  exchangeSettings,
  exchangeSettingsApi,
  paymentMethods,
  paymentMethodsApi,
  productionDevice,
  productionDeviceApi,
} from 'modules/eecs-go';
import { grexAccounts, grexApi } from 'modules/grex';
import { hknrAccount, hknrAccountApi } from 'modules/hknr';
import { marketHelp, marketHelpApi } from 'modules/market-help';
import { appMedia, mediaApi } from 'modules/media';
import { necsAccount, necsAccountApi } from 'modules/necs';
import { platform, platformApi } from 'modules/platform';
import { users, usersApi } from 'modules/users';
import { wallet, walletApi } from 'modules/wallet';

enableMapSet();

let store = configureStore({
  reducer: {
    [appTerms.name]: appTerms.reducer,
    [appMedia.name]: appMedia.reducer,
    [appChat.name]: appChat.reducer,
    [appDashboard.name]: appDashboard.reducer,
    [appInvitation.name]: appInvitation.reducer,
    [appNotification.name]: appNotification.reducer,
    [customNotification.name]: customNotification.reducer,
    [authSession.name]: authSession.reducer,
    [companies.name]: companies.reducer,
    [companyType.name]: companyType.reducer,
    [platform.name]: platform.reducer,
    [wallet.name]: wallet.reducer,
    [users.name]: users.reducer,
    [exchangeSettings.name]: exchangeSettings.reducer,
    [eecsWhiteList.name]: eecsWhiteList.reducer,
    [eecsCommon.name]: eecsCommon.reducer,
    [eecsFees.name]: eecsFees.reducer,
    [eecsCancellations.name]: eecsCancellations.reducer,
    [necsAccount.name]: necsAccount.reducer,
    [productionDevice.name]: productionDevice.reducer,
    [eecsGoCertificate.name]: eecsGoCertificate.reducer,
    [eecsTransaction.name]: eecsTransaction.reducer,
    [eecsDashboard.name]: eecsDashboard.reducer,
    [eecsForward.name]: eecsForward.reducer,
    [eecsPackage.name]: eecsPackage.reducer,
    [appDataExport.name]: appDataExport.reducer,
    [hknrAccount.name]: hknrAccount.reducer,
    [paymentMethods.name]: paymentMethods.reducer,
    [marketHelp.name]: marketHelp.reducer,
    [eecsProducts.name]: eecsProducts.reducer,
    [grexAccounts.name]: grexAccounts.reducer,

    // API

    [appTermsApi.reducerPath]: appTermsApi.reducer,
    [appChatApi.reducerPath]: appChatApi.reducer,
    [grexApi.reducerPath]: grexApi.reducer,
    [appDashboardApi.reducerPath]: appDashboardApi.reducer,
    [mediaApi.reducerPath]: mediaApi.reducer,
    [authSessionApi.reducerPath]: authSessionApi.reducer,
    [eecsCommonApi.reducerPath]: eecsCommonApi.reducer,
    [eecsFeesApi.reducerPath]: eecsFeesApi.reducer,
    [eecsCancellationsApi.reducerPath]: eecsCancellationsApi.reducer,
    [platformApi.reducerPath]: platformApi.reducer,
    [walletApi.reducerPath]: walletApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [appInvitationApi.reducerPath]: appInvitationApi.reducer,
    [appNotificationApi.reducerPath]: appNotificationApi.reducer,
    [companyTypeApi.reducerPath]: companyTypeApi.reducer,
    [exchangeSettingsApi.reducerPath]: exchangeSettingsApi.reducer,
    [eecsWhiteListApi.reducerPath]: eecsWhiteListApi.reducer,
    [eecsCompanyApi.reducerPath]: eecsCompanyApi.reducer,
    [necsAccountApi.reducerPath]: necsAccountApi.reducer,
    [productionDeviceApi.reducerPath]: productionDeviceApi.reducer,
    [eecsGoCertificateApi.reducerPath]: eecsGoCertificateApi.reducer,
    [eecsTransactionApi.reducerPath]: eecsTransactionApi.reducer,
    [eecsDashboardApi.reducerPath]: eecsDashboardApi.reducer,
    [eecsForwardApi.reducerPath]: eecsForwardApi.reducer,
    [eecsPackageApi.reducerPath]: eecsPackageApi.reducer,
    [appDataExportApi.reducerPath]: appDataExportApi.reducer,
    [hknrAccountApi.reducerPath]: hknrAccountApi.reducer,
    [companiesApi.reducerPath]: companiesApi.reducer,
    [paymentMethodsApi.reducerPath]: paymentMethodsApi.reducer,
    [customNotificationApi.reducerPath]: customNotificationApi.reducer,
    [marketHelpApi.reducerPath]: marketHelpApi.reducer,
    [eecsProductsApi.reducerPath]: eecsProductsApi.reducer,
  },
  devTools: false,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      createListenerMiddleware().middleware,
      appTermsApi.middleware,
      appChatApi.middleware,
      appDataExportApi.middleware,
      authSessionApi.middleware,
      appDashboardApi.middleware,
      platformApi.middleware,
      walletApi.middleware,
      usersApi.middleware,
      mediaApi.middleware,
      appNotificationApi.middleware,
      appInvitationApi.middleware,
      companyTypeApi.middleware,
      exchangeSettingsApi.middleware,
      eecsWhiteListApi.middleware,
      eecsCompanyApi.middleware,
      eecsCancellationsApi.middleware,
      eecsFeesApi.middleware,
      eecsCommonApi.middleware,
      hknrAccountApi.middleware,
      necsAccountApi.middleware,
      productionDeviceApi.middleware,
      eecsGoCertificateApi.middleware,
      eecsTransactionApi.middleware,
      eecsDashboardApi.middleware,
      eecsForwardApi.middleware,
      eecsPackageApi.middleware,
      companiesApi.middleware,
      paymentMethodsApi.middleware,
      customNotificationApi.middleware,
      marketHelpApi.middleware,
      eecsProductsApi.middleware,
      grexApi.middleware,
    ),
});

// @ts-ignore
window.store = store;

export default store;
